import _ from 'lodash';
import {
  INTENT_TYPES,
  SEGMENT_CONFIG,
  STRIKE_SELECTION_TYPES,
  BUILDER_QUANT_CONFIG_VERSION,
  STOPS_VALUE_TYPES,
  OPTION_TYPES,
  SL_TRAIL_TYPES
} from 'v2/common/constants/index';
import { PipeDefinitionUtility } from 'v2/common/quantConfig/index';
import { isWeeklyExpiry } from '../components/InstrumentDetails/indexInstrumentsChecker';
import { TRADE_TYPES } from 'modules/QuantBuilder/config';
import ApplicationDomainUtility from 'v3/common/utils/applicationDomain';

const { isCrypto } = ApplicationDomainUtility;
const currentBroker = window.currentUserBroker;

export const deltaExpiryTime = '17:28';

export const getEmptySignals = [
  {
    conditionOperator: '',
    leftOperand: '',
    operator: '',
    rightOperand: '',
  },
];

export const signalTypes = {
  entrySignal: 'entrySignal',
  exitSignal: 'exitSignal',
  adjustSignal: 'adjustSignal',
};

export const CONDITION_OPERATORS = ['and', 'or'];

export const defaultIntentInstrumentExpiry = (instrumentGroups, instrumentIndex) => ({
  index: 1,
  cycle: isCrypto ? 'daily' : isWeeklyExpiry(instrumentGroups, instrumentIndex) ? 'weekly' : 'monthly'
});

export const IntentLabelConfig = (() => {
  const config = {};

  return {
    get config() {
      return config;
    },
    setConfigNameAndValue(name, value) {
      config[name] = value;
    },
    reverse(name, value) {
      const label = config[name];
      if (Number(value) === Number(label) && Number(label) !== 0) {
        config[name] = Number(label) - 1;
        return;
      }

      if (value === label) {
        const newLabel = String.fromCharCode(label.charCodeAt(0) - 1);
        config[name] = newLabel;
      }
    },
    reset() {
      Object.keys(config).forEach((key) => {
        config[key] = 0;
      });
    }
  };
})();

export const IDENTIFIER_TYPES = {
  case: 'case',
  pipe: 'pipe'
};

export const getDefaultIdentifier = (name, identifierType) => {
  let newLabel;

  if (_.isUndefined(name)) {
    throw new Error('Name is required to get default identifier');
  }

  const isCase = identifierType === IDENTIFIER_TYPES.case;

  if (!IntentLabelConfig.config[name]) {
    newLabel = isCase ? 1 : 'A';
  } else {
    newLabel = isCase
      ? Number(IntentLabelConfig.config[name]) + 1
      : String.fromCharCode(IntentLabelConfig.config[name].charCodeAt(0) + 1);
  }

  IntentLabelConfig.setConfigNameAndValue(name, newLabel);
  return { label: `${newLabel}`, iteration: 0, caseIndex: isCase ? name : -1 };
};

export const getDefaultTarget = () => ({
  label: '',
  iteration: 0,
});

export const defaultStopGain = { type: STOPS_VALUE_TYPES.Percent, value: 0 };

export const defaultLinearTrail = {
  type: SL_TRAIL_TYPES.Linear,
  trailingValue: 0,
  adjustValue: 0
};

export const defaultStopLoss = {
  type: STOPS_VALUE_TYPES.Percent, value: 0, trail: defaultLinearTrail
};

export const getDefaultQuickAdjustments = () => ({
  signal: '',
  action: '',
  targets: [],
});

export const getDefaultIntent = (instrumentGroups, instrumentIndex) => ({
  type: INTENT_TYPES.OpenLeg,
  instrument: {
    type: isCrypto ? SEGMENT_CONFIG.future : SEGMENT_CONFIG.option,
    instrumentIndex: 0,
    expiry: defaultIntentInstrumentExpiry(instrumentGroups, instrumentIndex),
    strike: {
      type: STRIKE_SELECTION_TYPES.EquityBasedStrikeIndex.value,
      strikeIndex: 0,
      multiplier: 0,
    },
    optionType: OPTION_TYPES.CE,
  },
  target: getDefaultTarget(),
  side: 'buy',
  quantity: 1,
  stopGain: defaultStopGain,
  stopLoss: defaultStopLoss,
  waitUntilPremiumToIncreaseInPercentage: 0,
  waitUntilPremiumToDecreaseInPercentage: 0,
  waitAndTradeType: 'waitUntilPremiumToIncreaseInPercentage',
  reEntryCount: 0,
  reExecuteCount: 0,
  uiConfigs: { quickAdjustments: [] }
});

export const getDefaultModifyOrderIntent = (caseIndex) => ({
  type: INTENT_TYPES.CloseLeg,
  target: getDefaultTarget(),
  identifier: getDefaultIdentifier(caseIndex, IDENTIFIER_TYPES.case)
});

export const getDefaultBasketIntent = () => ({
  type: INTENT_TYPES.OpenBasket,
  name: '',
  // identifier: getDefaultIdentifier(),
  multiplier: 1,
});

export const getDefaultCaseIntent = (intentType) => ({
  type: INTENT_TYPES[intentType],
  caseIndex: null,
});
// when the intent is of type OpenBasket,
// the identifier have to call seperately and attach to the intent

export const defaultUiBehaviourFlags = {
  isReEntry: false,
  isReExecute: false,
  isTrailingStopLoss: false,
  isWaitAndTrade: false,
};

//TODO: Option contracts will expire by 5:30PM IST at their expiry day but market don't have exit time
//TODO: But this one is confusing...
export const getMarketExitTime = () => {
  return isCrypto ? deltaExpiryTime : '15:28';
};

export const getDefaultTimeBasedSignal = (() => {
  switch (true) {
    case ApplicationDomainUtility.isAlgoverse:
      return {
        entryTime: '09:15',
        exitTime: '15:14'
      };
    default:
      return {
        entryTime: '09:15',
        exitTime: '15:25'
      };
  }
})();

export const getDefaultExpiryHandling = {
  exitDaysBefore: 0,
  exitTime: getMarketExitTime(),
  legacyClose: isCrypto ? true : false,
};

export const getDefaultAdjustment = () => ({
  signal: [],
  intents: []
});

export const getDefaultCase = (caseIndex, instrumentGroups) => ({
  entry: {
    signal: [],
    intents: [{
      ...getDefaultIntent(instrumentGroups, 0),
      identifier: getDefaultIdentifier(caseIndex, IDENTIFIER_TYPES.case)
    }]
  },
  adjustments: [],
  exitSignal: [],
  uiConfigs: { isMoveToCost: false },
  isParallelCases: false
});

export const getQuantModes = {
  simple: { label: 'Simple', value: 'simple' },
  advanced: { label: 'Advanced', value: 'advanced' }
};

export const getDefaultQuantConfigUiConfigs = () => ({
  mode: getQuantModes.simple.value,
  segment: SEGMENT_CONFIG.equity
});

export const emptyQuantConfig = () => ({
  version: BUILDER_QUANT_CONFIG_VERSION,
  maxInstrumentsInGroup: 1,
  instrumentGroups: isCrypto ? getDefaultInstrumentsGroup() : [],
  tradingType: isCrypto ? TRADE_TYPES.positional : TRADE_TYPES.intraday,
  pipeConfigs: [PipeDefinitionUtility.getPipeConfigDefaults('currentCandle')],
  cases: [getDefaultCase(0)],
  uiBehaviourFlags: defaultUiBehaviourFlags,
  adjustmentOptions: { executionType: 'nextMinute' },
  uiConfigs: getDefaultQuantConfigUiConfigs(),
  bounds: {
    maxTransactionsPerDay: isCrypto ? 5 : 1,
    timeBasedSignal: isCrypto ? {} : getDefaultTimeBasedSignal,
    expiryHandling: getDefaultExpiryHandling,
  },
  entryOrderType: ApplicationDomainUtility.getUiConfig("default_quant_config_ordertype") ?? 'market',
  entryOrderModification: {},
  exitOrderType: ApplicationDomainUtility.getUiConfig("default_quant_config_ordertype") ?? 'market',
  exitOrderModification: {},
  isPlaceStoplossOrderToBroker: true,
  isPlaceOptionsEntryBuyOrdersFirst: isCrypto ? false : true,
  orderExecutionVersion: 'v2',
});

export const cryptoOptionsInstruments = ['DEX:BTCUSD', 'DEX:ETHUSD']
const getDefaultInstrumentsGroup = () => {
  if (currentBroker === 'coin_dcx') return [['CDCX:BTCUSD']]
  return [[cryptoOptionsInstruments[0]]]

}

export const hasCryptoOptionsInstrument = (instrumentGroups, instrumentIndex) =>
  _.some(instrumentGroups[instrumentIndex], (instrument) =>
    _.includes(cryptoOptionsInstruments, instrument.replace(/NFO:|BFO:/, ''))
  );
